'use client';

import { createZustandStore } from '../../../zustand';

import { IBidFilterState, ICategoryItem, IBrand } from '../types';

import {
  loadPersistedBidsFilterUIState,
  persistBidsFilterUIState,
} from './bidFilterLocalStorageUtils';
import { onClearBidFilterStoreCallback } from './callbacks';

/**
 * -----------------------------------------------------------------------------
 * Zustand store hook for managing bid filter state across the application.
 * Provides centralized state management for all bid-related filters, search,
 * sorting, and UI preferences.
 *
 * State Categories:
 * - Search & Context: bidSearchKey, contextId, hotDealsBidSearchKey
 * - Category Filters: categoryBidFilterList, selectedCategoryBidFilter
 * - Brand Filters: selectedBrandBidFilterList
 * - Asset Groups: selectedAssetGroups
 * - Date Range: dateRange
 * - Sorting: sortBy, orderBy, bidListSortFilterIndex, hotDealsBidListSortIndex
 * - Rank Filter: selectedBidRankFilter
 * - Tab Groups: selectedBidsTabGroup
 * - Table Preferences: bidsListTableColumnVisibility, bidsListTablePageSize,
 *   stagedBidsTableColumnVisibility, stagedBidsTablePageSize,
 *   uploadedBidsTableColumnVisibility, uploadedBidsTablePageSize
 * - UI State: isFiltersDialogVisible, isFiltersDialogPinned
 *
 * Usage:
 * ```tsx
 * const { selectedCategoryBidFilter, updateSelectedCategoryBidFilter } = useBidFilterStore();
 * ```
 */
export const useBidFilterStore = createZustandStore<IBidFilterState>()(
  (set) => {
    const { isFiltersDialogPinned } = loadPersistedBidsFilterUIState() || {
      isFiltersDialogPinned: false,
    };

    return {
      bidSearchKey: '',
      dateRange: null,
      updateBidSearchKey: (newKey) => set(() => ({ bidSearchKey: newKey })),
      contextId: null,
      updateContextId: (newContextId) =>
        set(() => ({ contextId: newContextId })),
      categoryBidFilterList: [],
      updateCategoryBidFilterList: (categories) =>
        set(() => ({ categoryBidFilterList: categories })),
      selectedBrandBidFilterList: [],
      updateSelectedBrandBidFilterList: (brands: IBrand[]) =>
        set(() => ({ selectedBrandBidFilterList: brands })),
      selectedCategoryBidFilter: null,
      updateSelectedCategoryBidFilter: (category: ICategoryItem | null) =>
        set(() => ({ selectedCategoryBidFilter: category })),
      hotDealsBidSearchKey: '',
      updateHotDealsBidsSearchKey: (newKey) =>
        set(() => ({ hotDealsBidSearchKey: newKey })),
      selectedHotDealsCategoryBidFilterIndex: -1,
      updateSelectedHotDealsCategoryBidFilterIndex: (categoryIndex: number) =>
        set(() => ({ selectedHotDealsCategoryBidFilterIndex: categoryIndex })),
      selectedHotDealsBrandIdBidFilterList: [],
      updateSelectedHotDealsBrandIdBidFilterList: (brandId) =>
        set(() => ({ selectedHotDealsBrandIdBidFilterList: [...brandId] })),
      selectedBidRankFilter: undefined,
      updateBidSelectedRankFilter: (rankType) =>
        set(() => ({ selectedBidRankFilter: rankType })),
      bidListSortFilterIndex: 0,
      updateBidListSortFilterIndex: (sortIndex: number) =>
        set(() => ({ bidListSortFilterIndex: sortIndex })),
      hotDealsBidListSortIndex: 1,
      updateHotDealsBidListSortFilterIndex: (sortIndex: number) =>
        set(() => ({ hotDealsBidListSortIndex: sortIndex })),
      onUpdateBidsDateRange: (dateRange) => set(() => ({ dateRange })),
      selectedBidsTabGroup: 'ALL_DEALS',
      updateSelectedBidsTabGroup: (tab) =>
        set(() => ({ selectedBidsTabGroup: tab })),
      selectedAssetGroups: [],
      updateSelectedAssetGroups: (assetGroups) =>
        set(() => ({ selectedAssetGroups: assetGroups })),
      sortBy: null,
      updateSortBy: (sortBy) => set(() => ({ sortBy })),
      orderBy: null,
      updateOrderBy: (orderBy) => set(() => ({ orderBy })),
      bidsListTableColumnVisibility: {},
      updateBidsListTableColumnVisibility: (visibility) =>
        set(() => ({ bidsListTableColumnVisibility: visibility })),
      bidsListTablePageSize: 20,
      updateBidsListTablePageSize: (pageSize) =>
        set(() => ({ bidsListTablePageSize: pageSize })),
      stagedBidsTableColumnVisibility: {},
      updateStagedBidsTableColumnVisibility: (visibility) =>
        set(() => ({ stagedBidsTableColumnVisibility: visibility })),
      stagedBidsTablePageSize: 20,
      updateStagedBidsTablePageSize: (pageSize) =>
        set(() => ({ stagedBidsTablePageSize: pageSize })),
      uploadedBidsTableColumnVisibility: {},
      updateUploadedBidsTableColumnVisibility: (visibility) =>
        set(() => ({ uploadedBidsTableColumnVisibility: visibility })),
      uploadedBidsTablePageSize: 20,
      updateUploadedBidsTablePageSize: (pageSize) =>
        set(() => ({ uploadedBidsTablePageSize: pageSize })),
      isFiltersDialogVisible: false,
      onUpdateIsFiltersDialogVisible: (isVisible) =>
        set(() => ({ isFiltersDialogVisible: isVisible })),
      isFiltersDialogPinned,
      onUpdateIsFiltersDialogPinned: (isPinned) => {
        set(() => {
          const newState = { isFiltersDialogPinned: isPinned };
          persistBidsFilterUIState(newState);

          return newState;
        });
      },
      clearBidsStore: onClearBidFilterStoreCallback(set),
    };
  }
);
